/** Helpers for 1:1 DM identity, member ids, and sidebar display. */

export const extractEntityId = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).trim();
    if (!text || text === '[object Object]') return '';
    return text;
  }
  if (typeof value === 'object') {
    return extractEntityId(value.id || value._id || value.$oid || value.oid);
  }
  return '';
};

export const normalizeMemberIds = (raw) => {
  if (raw == null || raw === '') return [];
  let list = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        list = JSON.parse(trimmed);
      } catch {
        list = raw.split(/[,\s]+/);
      }
    } else {
      list = raw.split(/[,\s]+/);
    }
  } else if (typeof raw === 'object') {
    try {
      const fromIter = typeof raw[Symbol.iterator] === 'function' ? Array.from(raw) : [];
      list = fromIter.length ? fromIter : Object.values(raw);
    } catch {
      list = Object.values(raw);
    }
  }
  return [...new Set(list.map(extractEntityId).filter(Boolean))];
};

export const channelMemberIds = (channel) => {
  const direct = normalizeMemberIds(channel?.memberIds);
  if (direct.length) return direct;
  if (typeof channel?.payloadJson === 'string') {
    try {
      return normalizeMemberIds(JSON.parse(channel.payloadJson)?.memberIds);
    } catch {
      // ignore
    }
  }
  return [];
};

export const isWorkloopDm = (channel) =>
  channel?.systemChannelKey === 'WORKLOOP'
  || String(channel?.name || '').trim().toLowerCase() === 'workloop';

export const userDisplayName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
  || user?.username
  || '';

const channelRecency = (channel) => {
  const ts = new Date(
    channel?.updatedAt || channel?.lastMessageAt || channel?.createdAt || 0
  ).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const channelDisplayName = (channel) =>
  String(channel?.name || '').trim().toLowerCase();

export const otherParticipantId = (channel, currentUserId) => {
  const me = extractEntityId(currentUserId);
  const members = channelMemberIds(channel);
  const others = members.filter((id) => id && id !== me);
  // 1:1 only. Workloop and leftover org-wide member lists must not
  // pick "the first other person" (that was collapsing everyone onto Alice).
  if (others.length === 1) return others[0];
  if (members.length === 1 && members[0] === me) return me;
  return extractEntityId(channel?.otherUserId);
};

export const isDirectMessageWithPeer = (channel, currentUserId, peerId) => {
  const peer = extractEntityId(peerId);
  const me = extractEntityId(currentUserId);
  if (!channel || !peer || isWorkloopDm(channel)) return false;
  const members = channelMemberIds(channel);
  const others = members.filter((id) => id && id !== me);
  if (others.length === 1) return others[0] === peer;
  if (members.length === 2) return members.includes(me) && members.includes(peer);
  return false;
};

export const findDirectMessageForPeer = (list, currentUserId, peerId) => {
  const peer = extractEntityId(peerId);
  if (!peer) return null;
  const rows = Array.isArray(list) ? list : [];
  return rows.find((channel) => isDirectMessageWithPeer(channel, currentUserId, peer)) || null;
};

export const mergePeopleForLookup = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((person) => {
    if (!person) return;
    const id = extractEntityId(person);
    if (!id) return;
    const existing = byId.get(id);
    byId.set(id, existing ? { ...existing, ...person } : person);
  });
  return Array.from(byId.values());
};

export const normalizeUserRecord = (user) => {
  if (!user || typeof user !== 'object') return user;
  const id = extractEntityId(user);
  return id ? { ...user, id, _id: user._id || id } : user;
};

export const findUserForDm = (channel, currentUserId, users = []) => {
  if (!channel || isWorkloopDm(channel)) return null;
  const list = Array.isArray(users) ? users : [];
  const otherId = otherParticipantId(channel, currentUserId);
  if (otherId) {
    const byId = list.find((user) => extractEntityId(user) === otherId);
    if (byId) return byId;
  }
  // Name is a fallback only when member ids do not identify a 1:1 peer.
  const name = channelDisplayName(channel);
  if (!name || name === 'unknown user' || name === 'workloop') return null;
  return list.find((user) => userDisplayName(user).toLowerCase() === name)
    || list.find((user) => String(user?.username || '').toLowerCase() === name)
    || null;
};

export const dmIdentityKey = (channel, currentUserId, users = []) => {
  if (isWorkloopDm(channel)) return 'sys:WORKLOOP';
  const peerId = otherParticipantId(channel, currentUserId);
  if (peerId) return `user:${peerId}`;
  const peer = findUserForDm(channel, currentUserId, users);
  if (extractEntityId(peer)) return `user:${extractEntityId(peer)}`;
  const name = channelDisplayName(channel);
  if (name && name !== 'unknown user') return `name:${name}`;
  const id = extractEntityId(channel);
  return id ? `id:${id}` : null;
};

/**
 * One sidebar row per 1:1 peer. Duplicate leftover rows that share the
 * same two member ids collapse. Rows with missing members can still
 * collapse by display name. A leftover named "Alice" that actually
 * contains Bob stays Bob.
 */
export const dedupeDirectMessages = (list, currentUserId, users = []) => {
  const byKey = new Map();
  (Array.isArray(list) ? list : []).forEach((channel, index) => {
    if (!channel) return;
    const id = extractEntityId(channel);
    const normalized = {
      ...channel,
      id: id || channel.id,
      _id: channel._id || id,
      memberIds: channelMemberIds(channel),
    };
    const key = dmIdentityKey(normalized, currentUserId, users) || `row:${id || index}`;
    const existing = byKey.get(key);
    if (!existing || channelRecency(normalized) >= channelRecency(existing)) {
      byKey.set(key, normalized);
    }
  });
  return Array.from(byKey.values());
};
